<template>
  <div class="dashboard-layout">
    <div class="divider"></div>
    <div class="data-section">
      <div class="filters-section">
        <div class='filters'>
          <advanced-filters-header
            layout='dashboard'
            @applyDomains='apply'
            @applyOrganizations='() => {}'
          />
          <div class="date-select" v-if="hasLicenseModule('dashboard_date_filter')">
            <custom-datepicker
              v-model="date"
              type="monthrange"
              range-separator="To"
              start-placeholder="Start month"
              end-placeholder="End month"
              :picker-options="pickerOptions"
              :title="$t('dashboard.header.select.date.outside').toString()"
            />
          </div>
        </div>
      </div>
      <div class="top-section">
        <div class="points-and-score-section">
          <div class='points'>
            <dashboard-assets-score
              :domains='domainFilter'
            />
          </div>
          <div class='risk-score' ref='riskScore'>
            <risk-score
              :graphWidth="dynamicRiscScoreGraphWidth"
              :value='totalPassiveRiskScore'
              :domains='domainFilter'
              :details='divisionDetailsPassiveRiskScore'
              :missing-data='dataMissingRiskScore'
              :optimized='optimized'
              :recalculate-required='recalculateRequired'
              @recalculate='recalculateRiskScore'
            />
          </div>
        </div>
      </div>
      <div class="action-to-take">
        <dashboard-actions-to-take/>
      </div>
      <div class="assets-section">
        <div class="assets-section__label">
          {{$t('dashboard.inventory.title')}}
        </div>
        <div class="assets-section__widgets">
          <assets-widget
            title="dashboard.assets.widget.title.organizations"
            :hide-details="true"
            :url='organizationsRoute'
            :data="`${organizations.length}`"
            :selected="`${selectedOrganizationsCount}`"
          />
          <assets-widget
            title="dashboard.assets.widget.title.tld"
            :hide-details="true"
            :url='configurationTldsRoute'
            :data="`${listOfDomains.length}`"
            :selected="`${selectedTldsCount}`"
          />
          <assets-widget
            title="dashboard.assets.widget.title.cloud.account"
            :hide-details="true"
            :url='configurationCloudAccountsRoute'
            :data="`${cloudAccountsStats.length}`"
            :selected="selectedCloudAccountsCount"
          />
          <assets-widget
            title="dashboard.assets.widget.title.subdomains"
            subtitle="dashboard.assets.widget.subtitle.subdomains"
            :data="`${subdomainsStatsForDisplay.alive} / ${subdomainsStatsForDisplay.total}`"
            :delta="subdomainsDelta"
            :url='configurationInfrastructureRoute'
            :no-data='!discoveryStatisticsExists'
          />
          <assets-widget
            title="dashboard.assets.widget.title.web"
            subtitle="dashboard.assets.widget.subtitle.web"
            :data="`${websitesStatsForDisplay.websites}`"
            :delta="websitesDelta"
            :url='configurationWebsitesRoute'
            :no-data='!discoveryStatisticsExists'
          />
          <assets-widget
            title="dashboard.assets.widget.title.cloud.storage"
            subtitle="dashboard.assets.widget.subtitle.cloud"
            :data="`${cloudAndBucketStatsForDisplay.bucket.exposed} / ${cloudAndBucketStatsForDisplay.bucket.total}`"
            :delta="cloudAndBucketDelta.bucket"
            :url='configurationBucketAssetsRoute'
            :no-data='!discoveryStatisticsExists'
          />
          <assets-widget
            title="dashboard.assets.widget.title.cloud.instance"
            subtitle="dashboard.assets.widget.subtitle.cloud"
            :data="`${cloudAndBucketStatsForDisplay.cloud.exposed} / ${cloudAndBucketStatsForDisplay.cloud.total}`"
            :delta="cloudAndBucketDelta.cloud"
            :url='configurationCloudAssetsRoute'
            :no-data='!discoveryStatisticsExists'
          />
        </div>
      </div>
      <div class="risk-score-history-section">
      </div>
      <div class="employee-and-risk-history-section">
          <div class="employee-and-risk-history-section__employee-widgets">
            <div class="employee-and-risk-history-section__label">{{$t('dashboard.header.leaked.data')}}</div>
              <employees-widget
                :actual="actual"
                :clearText="clearText"
                :leaked="leaked"
                :verified="verifiedEmployeesStats"
                :delta='employeesDelta'
                :date="new Date()"
                :url='configurationEmployeesRoute'
                :no-data='!employeesStatisticsExists'
              />
          </div>
            <div class="employee-and-risk-history-section__risk-history-widgets">
              <div class="employee-and-risk-history-section__label">{{$t('dashboard.header.risk.score.history')}}</div>
              <risk-score-history-widget
                :graph-width="dynamicLeaksDataGraphWidth"
                :data="formattedRiskScoreHistory"
              />
            </div>
      </div>
      <div class='technologies-section'>
        <div class='technologies-section__title'>
          <div class="technologies-section__title__label">{{$t('dashboard.technologies.title')}}</div>
          <div class="technologies-section__title__link">
            <span class="dashboard-link-btn">
              <router-link :to="configurationTechnologiesRoute">{{$t('dashboard.technologies.button.title')}}</router-link>
            </span>
          </div>
        </div>
        <div class="technologies-section__widgets">
          <TechnologiesWidget
            :table-categories="technologiesTableCategories"
            :items="technologiesForTable"
          />
        </div>
      </div>
      <div class="projects-and-scans-section">
        <div class="projects-and-scans-section__scans-widget">
          <div class="projects-and-scans-section__scans-widget__label">{{$t('dashboard.header.scans.data')}}</div>
          <AutomaticScansWidget
            :url='projectsRoute'
          />
        </div>
        <div class="projects-and-scans-section__projects-widget">
          <div class="projects-and-scans-section__projects-widget__label">{{$t('dashboard.header.projects.data')}}</div>
          <OrganizationsProjectsWidget
            :url='projectsRoute'
          />
        </div>
        <div class="projects-and-scans-section__schedule-widget">
          <div class="projects-and-scans-section__schedule-widget__header">
            <div class="projects-and-scans-section__schedule-widget__label">{{$t('dashboard.header.scans.schedule')}}</div>
            <button
              type="button"
              class="projects-and-scans-section__schedule-widget__view-toggle"
              :title="scanScheduleViewToggle.title"
              :aria-label="scanScheduleViewToggle.title"
              @click="toggleScanScheduleView"
            >
              <component :is="scanScheduleViewToggle.icon" aria-hidden="true" focusable="false" />
            </button>
          </div>
          <ScansScheduleWidget :is3d-view="isScanSchedule3dView" />
        </div>
      </div>
    </div>
    <div class="divider-right"></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  GET_RISK_SCORE_HISTORY,
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  SET_LOADING_DATA_FLAG,
} from '@/store/project/actionTypes';
import DashboardActionsToTake from '@/components/Dashboard/DashboradActionsToTake.vue';
import CustomDatepicker from '@/components/datepicker/CustomDatepicker.vue';
import DashboardAssetsScore from '@/components/Dashboard/DashboardAssetsCount.vue';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import { views } from '@/router/constants';
import {
  CLEAR_DOMAIN_FILTER,
  SET_DATE_FILTER,
  SET_DOMAIN_FILTER,
  SET_ORGANIZATION_FILTER,
} from '@/store/filters/actionTypes';
import {
  LOAD_GENERAL_STATISTICS,
  LOAD_PASSIVE_RISK_SCORE,
  LOAD_PROJECT_STATISTICS, RECALCULATE_RISK_SCORE, RECALCULATE_RISK_SCORE_BULK,
} from '@/store/statistics/actionTypes';
import RiskScore from '@/components/Dashboard/RiskScoreWidget.vue';
import EmployeesWidget from '@/components/Dashboard/EmployeesWidget.vue';
import AutomaticScansWidget from '@/components/Dashboard/AutomaticScansWidget.vue';
import OrganizationsProjectsWidget from '@/components/Dashboard/OrganizationsProjectsWidget.vue';
import AssetsWidget from '@/components/Dashboard/AssetsWidget.vue';
import TechnologiesWidget from '@/components/Dashboard/TechnologiesWidget.vue';
import ScansScheduleWidget from '@/components/Dashboard/ScansScheduleWidget.vue';
import View3dIcon from '@/assets/icons/view_3d.svg?inline';
import View2dIcon from '@/assets/icons/view_2d.svg?inline';
import { technologiesTableCategories } from '@/services/tableColumns';
import { LOAD_CENTRALIZED_ISSUES } from '@/store/centralizedIssues/actionTypes';
import RiskScoreHistoryWidget from '@/components/Dashboard/RiskScoreHistoryWidget.vue';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import { LOAD_ALL_ORGANIZATIONS } from '@/store/organization/actionTypes';
import { SIDEBAR_WIDTH_VW } from '@/constants/layout';

export default {
  name: 'DashboardLayout',
  components: {
    RiskScoreHistoryWidget,
    AdvancedFiltersHeader,
    TechnologiesWidget,
    AssetsWidget,
    OrganizationsProjectsWidget,
    AutomaticScansWidget,
    EmployeesWidget,
    RiskScore,
    CustomDatepicker,
    DashboardActionsToTake,
    DashboardAssetsScore,
    ScansScheduleWidget,
  },
  data() {
    return {
      projectsRoute: {name: views.ACTIVITY_LOG},
      configurationLeaksDataRoute: {name: views.INVENTORY, hash: '#leaks'},
      configurationEmployeesRoute: {name: views.INVENTORY, hash: '#employees'},
      configurationCloudAssetsRoute: {name: views.INVENTORY, hash: '#cloud', params: { tab: this.$t('configuration.cloud.assets.cloud.list') }},
      configurationBucketAssetsRoute: {name: views.INVENTORY, hash: '#cloud', params: { tab: this.$t('configuration.cloud.assets.buckets.list') }},
      configurationTechnologiesRoute: {name: views.INVENTORY, hash: '#technologies'},
      configurationTldsRoute: {name: views.INVENTORY, hash: '#topLevelDomain'},
      organizationsRoute: {name: views.SETTINGS, hash: '#organizations-settings'},
      configurationCloudAccountsRoute: {name: views.INVENTORY, hash: '#cloud_accounts'},
      configurationInfrastructureRoute: {name: views.INVENTORY, hash: '#infrastructure'},
      configurationWebsitesRoute: {name: views.INVENTORY, hash: '#web_application'},
      windowInnerWidth: window.innerWidth,
      polling: null,
      isScanSchedule3dView: false,
    };
  },
  async created() {
    // Initialize theme only after login (in dashboard)
    this.initTheme();

    if (this.profile) {
      await this.loadOrganizations();
      await this.loadDomains();
      await this.loadCloudAccounts();
      await this.loadProjectsStatistics();
      await this.loadStatisticsForDashboard();
      await this.loadPassiveRiskScore();
      await this.loadTopPriorityIssues();
    }
    window.addEventListener('resize', this.windowResizeHandler);
  },
  async mounted() {
    await this.setDefaultDomain();
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
  },
  computed: {
    ...mapGetters([
      'dashboardIssuesListLoading',
      'dataLoading',
      'dateFilter',
      'domainFilter',
      'profile',
      'domains',
      'userId',
      'advisedActions',
      'configurationDomains',
      'generalStatistics',
      'passiveRiskScore',
      'riskScoreHistoryPerTld',
      'hasLicenseModule',
      'companyData',
      'configurationCloudAccounts',
      'organizations',
      'organizationFilter',
      'dashboardIssuesRefId',
      'dashboardIssuesProgress',
      'dashboardIssuesRunningInterval',
    ]),
    ...mapGetters('theme', ['isDarkMode']),
    colorThemeComputed: {
      get() {
        return this.isDarkMode;
      },
      set(value) {
        this.setDarkMode(value);
      },
    },
    verifiedEmployeesStats() {
      let amountVerified = 0;
      this.passwordSprayStatistics.forEach(stats => {
        if (stats.statistics?.data?.issues && Object.keys(stats.statistics.data.issues).length) {
          Object.keys(stats.statistics.data.issues).forEach(key => {
            if (key !== 'isTldVulnerable' && Object.keys(stats.statistics.data.issues[key])?.length) {
              amountVerified += stats.statistics.data.issues[key].critical || 0;
            }
          });
        }
      });
      return amountVerified || 0;
    },
    selectedOrganizationsCount() {
      // Count against the loaded list so stale filter names don't inflate the number
      return this.organizations.filter(organization => this.organizationFilter.includes(organization.name)).length;
    },
    selectedTldsCount() {
      // domainFilter mixes TLDs and cloud accounts, so count only the TLDs in scope
      return this.listOfDomains.filter(domain => this.domainFilter.includes(domain)).length;
    },
    cloudAccountsStats() {
      return this.configurationCloudAccounts.filter(cloudAccount => this.organizationFilter.includes(cloudAccount.organization.name));
    },
    selectedCloudAccountsCount() {
      // null keeps the single-number view when there are no cloud accounts to select from
      if (this.cloudAccountsStats.length === 0) return null;
      return `${this.cloudAccountsStats.filter(cloudAccount => this.domainFilter.includes(cloudAccount.configData)).length}`;
    },
    totalPassiveRiskScore() {
      const tldKeys = Object.keys(this.passiveRiskScore);
      if (tldKeys.length) {
        const scoreArray = tldKeys.map(key => parseFloat(this.passiveRiskScore[key].score));
        if (scoreArray.length) {
          const sumOfScores = scoreArray.reduce((a, b) => a + b) / scoreArray.length;
          const fixedSum = (100 - sumOfScores).toFixed(1);
          return parseFloat(fixedSum);
        }
      }
      return 100;
    },
    optimized() {
      const riskScoreValues = Object.values(this.passiveRiskScore);
      return riskScoreValues.some(riskScoreObj => riskScoreObj.optimized);
    },
    recalculateRequired() {
      const riskScoreValues = Object.values(this.passiveRiskScore);
      return riskScoreValues.some(riskScoreObj => riskScoreObj.recalculateRequired);
    },
    dataMissingRiskScore() {
      const tldKeys = Object.keys(this.passiveRiskScore);
      const missingDataDetailsRiskScoreObject = {
        hijack: true,
        smtp: true,
        dns: true,
        cloud: true,
        dataleaks: true,
        employees: true,
        waf: true,
        tech: true,
        ports: true,
      };
      const riskScoreParameters = Object.keys(missingDataDetailsRiskScoreObject);
      if (tldKeys.length) {
        riskScoreParameters.forEach(param => {
          tldKeys.forEach(tld => {
            if (this.passiveRiskScore[tld].dataMissing) {
              if (!this.passiveRiskScore[tld].dataMissing?.[param]) {
                missingDataDetailsRiskScoreObject[param] = false;
              }
            }
          });
        });
      }
      return missingDataDetailsRiskScoreObject;
    },
    divisionDetailsPassiveRiskScore() {
      const tldKeys = Object.keys(this.passiveRiskScore);
      const detailsRiskScoreObject = {
        hijack: 0,
        smtp: 0,
        dns: 0,
        cloud: 0,
        dataleaks: 0,
        employees: 0,
        waf: 0,
        tech: 0,
        ports: 0,
      };
      const riskScoreParameters = Object.keys(detailsRiskScoreObject);
      if (tldKeys.length) {
        tldKeys.forEach(tld => {
          riskScoreParameters.forEach(param => {
            detailsRiskScoreObject[param] += this.passiveRiskScore[tld].details[param] || 0;
          });
        });
        if (tldKeys.length) {
          riskScoreParameters.forEach(param => {
            detailsRiskScoreObject[param] = parseFloat((detailsRiskScoreObject[param] / tldKeys.length).toFixed(1));
          });
        }
      }
      return detailsRiskScoreObject;
    },
    pickerOptions() {
      return {
        disabledDate: this.disabledDates,
        shortcuts: [
          {
            text: this.$t('date.picker.shortcut.this.month'),
            onClick(picker) {
              picker.$emit('pick', [new Date(), new Date()]);
            },
          }, {
            text: this.$t('date.picker.shortcut.two.months'),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 1);
              start.setDate(1);
              picker.$emit('pick', [start, end]);
            },
          }, {
            text: this.$t('date.picker.shortcut.three.months'),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 2);
              start.setDate(1);
              picker.$emit('pick', [start, end]);
            },
          },
        ],
      };
    },
    date: {
      get() {
        return this.dateFilter;
      },
      async set(value) {
        await this.$store.dispatch(SET_DATE_FILTER, value);
      },
    },
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
    statisticsForAllTlds() {
      return Object.values(this.generalStatistics);
    },
    discoveryStatisticsExists() {
      return this.statisticsForAllTlds.some(tldStats => Object.keys(tldStats).includes('discovery'));
    },
    employeesStatisticsExists() {
      return this.statisticsForAllTlds.some(tldStats => Object.keys(tldStats).includes('employees'));
    },
    dataLeakStatisticsExists() {
      return this.statisticsForAllTlds.some(tldStats => Object.keys(tldStats).includes('dataleaks'));
    },
    passwordSprayStatistics() {
      return this.statisticsForAllTlds.map(statistics => statistics.passwordSpray).filter(item => item);
    },
    employeesStatistics() {
      return this.statisticsForAllTlds.map(statistics => statistics.employees).filter(item => item);
    },
    allDiscoveryStatistics() {
      return this.statisticsForAllTlds.map(statistics => statistics.discovery).filter(item => item);
    },
    allLeakDataStatistics() {
      return this.statisticsForAllTlds.map(statistics => statistics.dataleaks).filter(item => item);
    },
    threeMonthEmployeesStatistics() {
      return this.generalStatistics.threeMonthsEmployeesLeakStats || [0, 0, 0];
    },
    threeMonthDataLeaksStatistics() {
      return this.generalStatistics.threeMonthsDataLeaksStats || [0, 0, 0];
    },
    employeesDelta() {
      let sumAllDeltas = 0;
      this.employeesStatistics.forEach(stats => {
        sumAllDeltas += (stats.delta?.data?.employees?.leaked || 0);
      });
      const sign = sumAllDeltas >= 0 ? 1 : -1;
      return this.calculateDelta(sumAllDeltas, this.leaked, sign);
    },
    leaked() {
      let amountLeaked = 0;
      this.employeesStatistics.forEach(stats => {
        amountLeaked += (stats.statistics?.data?.employees?.leaked || 0);
      });
      return amountLeaked;
    },
    clearText() {
      let amountClearText = 0;
      this.employeesStatistics.forEach(stats => {
        amountClearText += (stats.statistics?.data?.employees?.clearText || 0);
      });
      return amountClearText;
    },
    verified() {
      return 0;
    },
    identified() {
      let amountIdentified = 0;
      this.employeesStatistics.forEach(stats => {
        amountIdentified += (stats.statistics?.data?.employees?.identified || 0);
      });
      return amountIdentified;
    },
    actual() {
      return this.identified;
    },
    subdomainsStatsForDisplay() {
      let amountSubdomainsAlive = 0;
      let amountSubdomains = 0;
      this.allDiscoveryStatistics.forEach(stats => {
        amountSubdomainsAlive += (stats.statistics?.data?.subdomains?.alive || 0);
        amountSubdomains += (stats.statistics?.data?.subdomains?.total || 0);
      });
      return { total: amountSubdomains, alive: amountSubdomainsAlive };
    },
    dataLeaksStatsForDisplay() {
      let amountDataLeaks = 0;
      this.allLeakDataStatistics.forEach(stats => {
        amountDataLeaks += (stats.statistics?.data?.dataleaks?.total || 0);
      });
      return amountDataLeaks;
    },
    technologiesTableCategories() {
      return [
        technologiesTableCategories.technology,
        technologiesTableCategories.amount,
        technologiesTableCategories.exploitable,
      ];
    },
    technologiesStats() {
      const technologiesData = {};
      this.allDiscoveryStatistics.forEach(stats => {
        if (stats.statistics?.data?.technologies) {
          const technologiesKeys = Object.keys(stats.statistics.data.technologies);
          technologiesKeys.forEach(tech => {
            if (tech !== 'summary') {
              if (!Object.keys(technologiesData).includes(tech)) {
                technologiesData[tech] = {
                  technology: tech,
                  amount: stats.statistics.data.technologies[tech].assetsAmount,
                  exploitable: stats.statistics.data.technologies[tech].vulnerable || false,
                };
              } else {
                technologiesData[tech] = {
                  ...technologiesData[tech],
                  amount: technologiesData[tech].amount + stats.statistics.data.technologies[tech].assetsAmount,
                  exploitable: technologiesData[tech].exploitable || (stats.statistics.data.technologies[tech].vulnerable || false),
                };
              }
            }
          });
        }
      });
      const maxItems = Object.keys(technologiesData).map(tech => [tech, technologiesData[tech]]);
      maxItems.sort((first, second) => {
        const firstExploit = first[1].exploitable;
        const secondExploit = second[1].exploitable;
        if (firstExploit && !secondExploit) {
          return -1;
        }
        if (!firstExploit && secondExploit) {
          return 1;
        }
        return second[1].amount - first[1].amount;
      });
      if (maxItems.length > 2) {
        return maxItems.map(item => item[1]).slice(0, (this.windowInnerWidth - 200) / 200);
      }
      return maxItems.map(item => item[1]);
    },
    technologiesForTable() {
      return this.technologiesStats;
    },
    dataLeaksDelta() {
      let sumAllDeltas = 0;
      this.allLeakDataStatistics.forEach(stats => {
        sumAllDeltas += (stats.delta?.data?.dataleaks?.total || 0);
      });
      const sign = sumAllDeltas >= 0 ? 1 : -1;
      if (sign > 0) {
        return sumAllDeltas;
      }
      return 0;
    },
    subdomainsDelta() {
      let sumAllDeltas = 0;
      this.allDiscoveryStatistics.forEach(stats => {
        sumAllDeltas += (stats.delta?.data?.subdomains?.total || 0);
      });
      const sign = sumAllDeltas >= 0 ? 1 : -1;
      return this.calculateDelta(sumAllDeltas, this.subdomainsStatsForDisplay.total, sign);
    },
    websitesStatsForDisplay() {
      let amountWebsites = 0;
      this.allDiscoveryStatistics.forEach(stats => {
        amountWebsites += (stats.statistics?.data?.websites?.web || 0);
      });
      return { websites: amountWebsites};
    },
    websitesDelta() {
      let sumAllDeltas = 0;
      this.allDiscoveryStatistics.forEach(stats => {
        sumAllDeltas += (stats.delta?.data?.websites?.web || 0);
      });
      const sign = sumAllDeltas >= 0 ? 1 : -1;
      const sumAllPreviousScans = this.websitesStatsForDisplay.websites;
      return this.calculateDelta(sumAllDeltas, sumAllPreviousScans, sign);
    },
    cloudAndBucketStatsForDisplay() {
      const amountCloud = {
        exposed: 0,
        total: 0,
      };
      const amountBucket = {
        exposed: 0,
        total: 0,
      };
      this.allDiscoveryStatistics.forEach(stats => {
        amountCloud.total += (stats.statistics?.data?.cloudAssets?.summary?.total || 0);
        amountCloud.exposed += (stats.statistics?.data?.cloudAssets?.summary?.total_exposed || 0);
        amountBucket.exposed += (stats.statistics?.data?.buckets?.summary?.total_exposed || 0);
        amountBucket.total += (stats.statistics?.data?.buckets?.summary?.total || 0);
      });
      return { cloud: amountCloud, bucket: amountBucket };
    },
    cloudAndBucketDelta() {
      let sumAllDeltaCloud = 0;
      let sumAllDeltaBucket = 0;
      this.allDiscoveryStatistics.forEach(stats => {
        sumAllDeltaCloud += (stats.delta?.data?.cloudAssets?.summary?.total || 0);
        sumAllDeltaBucket += (stats.delta?.data?.buckets?.summary?.total || 0);
      });
      const signCloud = sumAllDeltaCloud >= 0 ? 1 : -1;
      const signBucket = sumAllDeltaBucket >= 0 ? 1 : -1;
      return {
        cloud: signCloud * this.calculateDelta(sumAllDeltaCloud, this.cloudAndBucketStatsForDisplay.cloud.total, signCloud),
        bucket: signBucket * this.calculateDelta(sumAllDeltaBucket, this.cloudAndBucketStatsForDisplay.bucket.total, signBucket),
      };
    },
    startMonth() {
      return this.startDate.getMonth();
    },
    startYear() {
      return this.startDate.getFullYear();
    },
    endMonth() {
      return this.endDate.getMonth();
    },
    endYear() {
      return this.endDate.getFullYear();
    },
    startDate() {
      if (this.date !== '') {
        return this.date[0];
      }
      return new Date();
    },
    endDate() {
      if (this.date !== '') {
        return this.date[1];
      }
      return new Date();
    },
    listOfDomains() {
      const domainsSet = new Set();
      this.configurationDomains.forEach(configurationDomain => {
        if (this.organizationFilter.includes(configurationDomain.organization.name)) {
          domainsSet.add(configurationDomain.configData);
        }
      });
      if (this.organizationFilter.includes(this.companyData.name) &&
        this.domains?.length &&
        !domainsSet.has(this.domains[0])
      ) {
        domainsSet.add(this.domains[0]);
      }
      return Array.from(domainsSet);
    },
    dynamicRiscScoreGraphWidth() {
      const sidebarWidth = Math.round(this.windowInnerWidth * SIDEBAR_WIDTH_VW);
      return Math.floor((this.windowInnerWidth - sidebarWidth - 420) * 0.95);
    },
    dynamicLeaksDataGraphWidth() {
      const sidebarWidth = Math.round(this.windowInnerWidth * SIDEBAR_WIDTH_VW);
      return Math.floor(this.windowInnerWidth - sidebarWidth - 370 - 80);
    },
    formattedRiskScoreHistory() {
      return this.riskScoreHistoryPerTld.pastYear;
    },
    scanScheduleViewToggle() {
      const targetView = this.isScanSchedule3dView ? '2d' : '3d';
      return {
        icon: targetView === '3d' ? View3dIcon : View2dIcon,
        title: this.$t(`dashboard.scans.schedule.view.switch.${targetView}`),
      };
    },
  },
  methods: {
    ...mapActions('theme', ['setDarkMode', 'initTheme']),
    toggleScanScheduleView() {
      this.isScanSchedule3dView = !this.isScanSchedule3dView;
    },
    async recalculateRiskScore(domains) {
      const actionName = domains.length > 1 ? RECALCULATE_RISK_SCORE_BULK : RECALCULATE_RISK_SCORE;
      await this.$store.dispatch(actionName, domains);
    },
    disabledDates(date) {
      return date > new Date();
    },
    calculateDelta(delta, total, sign) {
      if (sign < 0) {
        return (sign * Math.round(((sign * delta) / (total - delta)) * 100)) || 0;
      }
      return (sign * Math.round(((sign * delta) / total) * 100)) || 0;
    },
    dispatch(e) {
      this.$emit('input', e);
      this.domainSelected = e;
    },
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    async setDefaultDomain() {
      if (!this.organizationFilter || this.organizationFilter.length === 0) {
        if (this.companyData?.name?.length > 0) { // IF WE HAVE DEFAULT ORGANIZATION
          await this.$store.dispatch(SET_ORGANIZATION_FILTER, Array.from(new Set([this.companyData.name])));
          if (this.domains.length > 0) {
            await this.$store.dispatch(SET_DOMAIN_FILTER, Array.from(new Set(this.domains)));
          }
        } else {
          setTimeout(this.setDefaultOrganization, 300);
        }
      }
    },
    async loadStatisticsForDashboard() {
      await this.$store.dispatch(LOAD_GENERAL_STATISTICS,
        {
          domain: this.domainSelected.length > 0 ? this.domainSelected : [this.domains[0]],
          timeRange: {
            startMonth: this.startMonth,
            startYear: this.startYear,
            endMonth: this.endMonth,
            endYear: this.endYear,
          },
        });
    },
    async loadProjectsStatistics() {
      await this.$store.dispatch(LOAD_PROJECT_STATISTICS,
        {
          domain: this.domainSelected.length > 0 ? this.domainSelected : [this.domains[0]],
        });
    },
    async loadPassiveRiskScore() {
      await this.$store.dispatch(LOAD_PASSIVE_RISK_SCORE,
        {
          domain: this.domainSelected.length > 0 ? this.domainSelected : [this.domains[0]],
        });
      await this.$store.dispatch(GET_RISK_SCORE_HISTORY,
        {
          domain: this.domainSelected.length > 0 ? this.domainSelected : [this.domains[0]],
        });
    },
    async loadOrganizations() {
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
    },
    async loadDomains() {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
    },
    async loadCloudAccounts() {
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        noLimit: true,
      });
    },
    async apply() {
      await this.$store.dispatch(SET_LOADING_DATA_FLAG, true);
      await this.loadProjectsStatistics();
      await this.loadStatisticsForDashboard();
      await this.loadPassiveRiskScore();
      await this.loadTopPriorityIssues();
    },
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    async loadTopPriorityIssues() {
      this.clearInterval();
      await this.$store.dispatch(LOAD_CENTRALIZED_ISSUES, {
        page: 1,
        limit: 5,
        forDashboard: true,
        domain: this.domainSelected.length > 0 ? this.domainSelected : this.domains,
        hideSuppress: true,
        hideFixed: true,
        onlyCVEs: false,
        issueTypes: [],
        search: '',
        turnOnLoading: true,
        sort: {
          priorityScore: 'desc',
        },
      });
      if (this.dashboardIssuesRefId && this.dashboardIssuesRefId !== '') {
        this.polling = setInterval(async () => {
          if (!this.dashboardIssuesRefId || this.dashboardIssuesRefId === '') {
            this.clearInterval();
          } else if (!this.dashboardIssuesRunningInterval) {
            await this.$store.dispatch(LOAD_CENTRALIZED_ISSUES, {
              page: 1,
              limit: 5,
              forDashboard: true,
              domain: this.domainSelected.length > 0 ? this.domainSelected : this.domains,
              hideSuppress: true,
              hideFixed: true,
              onlyCVEs: false,
              issueTypes: [],
              search: '',
              turnOnLoading: true,
              refId: this.dashboardIssuesRefId,
              sort: {
                priorityScore: 'desc',
              },
            });
          }
        }, 3000);
      }
    },
  },
  watch: {
    async organizationFilter() {
      await this.$store.dispatch(CLEAR_DOMAIN_FILTER);
    },
    async profile() {
      if (this.profile) {
        await this.loadOrganizations();
        await this.loadDomains();
        await this.loadCloudAccounts();
        await this.loadProjectsStatistics();
        await this.loadStatisticsForDashboard();
        await this.loadPassiveRiskScore();
        await this.loadTopPriorityIssues();
      }
    },
    async date() {
      await this.loadStatisticsForDashboard();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.dashboard-layout {
  display: flex;
  height: 100%;
  background-color: var(--bg-primary);
}

.divider {
  flex: 0 0 2%;
  &-right{
    flex: 0 0 2%;
  }
}

.data-section {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.filters-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: $space-3;
  flex-wrap: wrap;
  gap: $space-2;

  .filters {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    min-width: 0;
  }
}

.date-select {
  margin-top: $space-2;
  margin-left: $space-8;

  :deep(.custom-datepicker) {
    .datepicker {
      background-color: var(--bg-widget);
    }
  }
}

.color-theme-switch {
  margin-right: $space-8; // Prevent overlap with logo on the right

  // Scoped overrides for v-switch
  :deep(.v-input--switch--inset) {
    .v-input--switch__track,
    .v-input--selection-controls__input {
      width: rem(72);
      height: rem(32);
      margin-top: rem(-3);
    }

    .v-input--switch__track {
      background-color: var(--bg-tertiary);
      opacity: 1;
      outline: 2px solid var(--border-primary);
      outline-offset: 0;
    }
  }

  :deep(.v-input--switch__thumb) {
    height: rem(24);
    width: rem(24);
    top: calc(50% - #{rem(13)});
    z-index: 5;
    background-color: #FFFFFF;
  }

  :deep(.v-input__icon) {
    .v-icon {
      z-index: 3;
      font-size: rem(24);
      pointer-events: none;
    }
  }

  // Sun icon - always yellow
  :deep(.v-input__prepend-outer) {
    margin-right: rem(-25);
    .v-icon {
      color: #F9C12E;
    }
  }

  // Moon icon - dark in light mode, light in dark mode
  :deep(.v-input__append-outer) {
    margin-left: rem(-39);
    .v-icon {
      color: var(--text-primary);
    }
  }

  // Active state overrides (when toggled ON = dark mode)
  :deep(.v-input--is-dirty) {
    .v-input--selection-controls__input {
      .v-input--switch__thumb {
        transform: translate(rem(40), 0) !important; // Override Vuetify's 20px default
        background-color: #FFFFFF;
        z-index: 5;
      }
      .v-input--selection-controls__ripple {
        transform: translate(rem(40), 0);
      }
    }
  }

  // Hide ripple container
  :deep(.v-ripple__container) {
    display: none;
  }
}

// Dark mode specific styles
[data-theme="dark"] {
  .color-theme-switch {
    :deep(.v-input__append-outer) {
      .v-icon {
        color: #E8EAED; // Light moon in dark mode
      }
    }
  }
}

.top-section {
  padding-top: 1.5%;
  padding-bottom: 1.5%;
  flex: 0 0 7%;
  display: flex;
  justify-content: space-between;

  .points-and-score-section {
    display: flex;
    flex-direction: row;
    width: 100%;
    max-height: rem(129);

    .risk-score {
      width: inherit;
      padding-left: $space-2;
      flex-grow: 1;
    }
  }
}

.action-to-take {
  padding-bottom: $space-5;
  display: flex;
}

.dashboard-link-btn {
  color: var(--brand-secondary);
  font-family: $font-family-medium;
  font-size: $font-size-sm;
  font-weight: 500;
  letter-spacing: 0;
  line-height: rem(15);

  &:hover {
    cursor: pointer;
  }

  a {
    color: inherit;
    text-decoration: none;
  }
}

.technologies-section {
  padding-bottom: $space-8;

  &__title {
    display: flex;
    align-items: center;
    margin-bottom: $space-3;

    &__label {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-md;
      font-weight: bold;
      line-height: $space-5;
      margin-right: $space-2;
      letter-spacing: 0;
    }

    .dashboard-link-btn {
      display: inline-flex;
      align-items: center;
    }
  }
}

.projects-and-scans-section {
  padding-bottom: $space-8;
  display: flex;
  flex-direction: row;

  &__scans-widget {
    margin-right: $space-6;
    flex: 1.5;

    &__label {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-md;
      font-weight: bold;
      line-height: $space-5;
      letter-spacing: 0;
      margin-bottom: $space-3;
    }
  }

  &__projects-widget {
    margin-right: $space-6;
    flex: 1.5;

    &__label {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-md;
      font-weight: bold;
      line-height: $space-5;
      letter-spacing: 0;
      margin-bottom: $space-3;
    }
  }

  &__schedule-widget {
    flex: 0.8;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: $space-3;
    }

    &__label {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-md;
      font-weight: bold;
      line-height: $space-5;
      letter-spacing: 0;
    }

    &__view-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      background: none;
      color: var(--text-muted);
      cursor: pointer;
      transition: color $transition-duration-fast $transition-timing-ease;

      // The icon is taller than the title; negative margins keep the header at the title's height so the chart doesn't shift
      margin: calc((#{$space-5} - #{$space-6}) / 2) 0;

      svg {
        width: $space-8;
        height: $space-6;
      }

      &:hover,
      &:focus-visible {
        color: var(--text-primary);
      }
    }
  }
}

.scans-schedule-section {
  padding-bottom: $space-8;

  &__label {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-md;
    font-weight: bold;
    line-height: $space-5;
    letter-spacing: 0;
    margin-bottom: $space-3;
  }
}

.assets-section {
  padding-bottom: $space-8;

  &__label {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-md;
    font-weight: bold;
    line-height: $space-5;
    letter-spacing: 0;
    margin-bottom: $space-3;
  }

  &__widgets {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    flex-wrap: wrap;
    gap: $space-3;

    .assets-widget {
      margin-right: 0;
    }
  }
}

.employee-and-risk-history-section {
  padding-bottom: rem(50);
  display: flex;
  flex-direction: row;

  &__label {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-md;
    font-weight: bold;
    line-height: $space-5;
    letter-spacing: 0;
    margin-bottom: $space-3;
  }

  &__risk-history-widgets {
    max-height: rem(208);
    margin-left: $space-8;
    // Fix: Ensure chart fits
    flex-grow: 1;
  }

  &__employee-widgets {
    display: flex;
    flex-direction: column;
    max-height: rem(208);
  }
}

.dashboard-domain-select {
  min-width: rem(250);
  max-width: rem(250);
}
</style>
